import { Injectable } from '@angular/core';
import { DataGridColumn, BuilderNode, BuilderComponentType } from '../interfaces/builder-screen.models';

export interface AnalyzedField {
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea';
  dataPath: string;
  label: string;
  isNested: boolean;
  nestedFields?: AnalyzedField[];
  isArray?: boolean;
  sampleValue?: any;
  // NOVO: Informações para renderizar array como DataGrid
  arrayColumns?: DataGridColumn[];
  arrayData?: any[];
}

export interface AnalyzedEntity {
  name: string;
  dataPath: string;
  fields: AnalyzedField[];
  sampleData: any[];
  suggestedColumns: DataGridColumn[];
  suggestedFormFields: BuilderNode[];
  // NOVO: Para suportar hierarquia
  parentEntity?: string; // ID da entidade pai (se for array aninhado)
  level: number; // Nível de aninhamento (0 = raiz, 1 = primeiro nível, etc)
  childEntities?: AnalyzedEntity[]; // Entidades filhas (arrays dentro deste array)
  // NOVO: Para arrays de primitivos (strings, numbers)
  isPrimitiveArray?: boolean; // Se true, é um array de primitivos transformado
  originalFieldName?: string; // Nome original do campo (para usar no header)
  parentContext?: string; // Contexto do objeto pai (ex: "image" em "image.image_id_list")
}

export interface ScreenAnalysisResult {
  entities: AnalyzedEntity[];
  suggestedLayout: {
    tabs: { id: string; name: string; }[];
    panels: { id: string; name: string; tab: string; }[];
  };
  dataSourcePath: string;
}

@Injectable({ providedIn: 'root' })
export class ScreenAnalyzerService {

  /**
   * Analisa JSON e retorna estrutura da tela
   * ⭐ MELHORADO: Suporta arrays aninhados profundos
   */
  analyzeJSON(json: any): ScreenAnalysisResult {
    console.log('🔍 Analisando JSON:', json);

    // 1. Encontrar entidades hierárquicas (arrays em todos os níveis)
    const entities = this.findEntitiesDeep(json);

    // Se não encontrou entidades de array, tratar JSON como entidade única
    if (entities.length === 0) {
      console.log('📌 Nenhum array encontrado. Tratando JSON como entidade única.');
      entities.push({
        name: 'Data',
        path: '',
        data: [json],
        level: 0
      });
    }

    console.log(`📊 Total de ${entities.length} entidades encontradas:`, entities.map(e => `${e.name} (nível ${e.level})`));

    // 2. Para cada entidade, analisar campos
    const analyzedEntities = entities.map(entity => this.analyzeEntityDeep(entity));

    // 3. Sugerir layout hierárquico (tabs, panels)
    const suggestedLayout = this.suggestLayoutDeep(analyzedEntities);

    return {
      entities: analyzedEntities,
      suggestedLayout,
      dataSourcePath: this.findRootDataPath(json)
    };
  }

  /**
   * ⭐ NOVO: Encontra TODOS os arrays (entidades) no JSON, incluindo arrays dentro de arrays
   * Faz busca recursiva profunda em todos os níveis
   */
  private findEntitiesDeep(
    obj: any,
    path: string = '',
    level: number = 0,
    parentEntity?: string
  ): Array<{ name: string; path: string; data: any[]; level: number; parentEntity?: string; isPrimitiveArray?: boolean; originalFieldName?: string; parentContext?: string }> {
    const entities: Array<{ name: string; path: string; data: any[]; level: number; parentEntity?: string; isPrimitiveArray?: boolean; originalFieldName?: string; parentContext?: string }> = [];

    const search = (current: any, currentPath: string, currentLevel: number, currentParent?: string) => {
      if (!current || typeof current !== 'object') return;

      for (const key in current) {
        const value = current[key];
        const newPath = currentPath ? `${currentPath}.${key}` : key;

        // Array de objetos = entidade!
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          const entityName = this.formatEntityName(key);
          
          console.log(`  📦 Array de objetos detectado: "${entityName}" (nível ${currentLevel}, path: ${newPath})`);
          
          entities.push({
            name: entityName,
            path: newPath,
            data: value,
            level: currentLevel,
            parentEntity: currentParent
          });

          // IMPORTANTE: Continuar buscando arrays DENTRO deste array
          // Analisar o primeiro item do array para encontrar arrays aninhados
          if (value[0]) {
            search(value[0], newPath, currentLevel + 1, entityName);
          }
        }
        // NOVO: Array de primitivos (strings, numbers) TAMBÉM é entidade!
        else if (Array.isArray(value) && value.length > 0) {
          const entityName = this.formatEntityName(key);
          
          console.log(`  📝 Array de primitivos detectado: "${entityName}" (nível ${currentLevel}, path: ${newPath}, tipo: ${typeof value[0]})`);
          
          // Transformar array de primitivos em array de objetos para facilitar renderização
          const transformedData = value.map((item: any, index: number) => ({
            index: index + 1,
            value: item
          }));
          
          // Extrair nome do objeto pai para diferenciar arrays com mesmo nome
          // Ex: "response.item_list.image.image_id_list" -> parentContext = "image"
          const pathParts = newPath.split('.');
          const parentContext = pathParts.length > 1 ? pathParts[pathParts.length - 2] : '';
          
          entities.push({
            name: entityName,
            path: newPath,
            data: transformedData, // Array transformado
            level: currentLevel,
            parentEntity: currentParent,
            isPrimitiveArray: true, // Flag para identificar arrays de primitivos
            originalFieldName: key, // Nome original do campo para usar no header
            parentContext: parentContext // Contexto do objeto pai para evitar duplicação de nomes
          });
        }
        // Objeto: continuar buscando dentro dele
        else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          search(value, newPath, currentLevel, currentParent);
        }
      }
    };

    console.log('🔎 Iniciando busca recursiva profunda por arrays...');
    search(obj, path, level, parentEntity);
    console.log(`✅ Busca concluída. ${entities.length} entidades encontradas.`);
    
    return entities;
  }

  /**
   * ⭐ NOVO: Analisa uma entidade específica com suporte a hierarquia
   */
  private analyzeEntityDeep(entity: { 
    name: string; 
    path: string; 
    data: any[]; 
    level: number; 
    parentEntity?: string;
    isPrimitiveArray?: boolean;
    originalFieldName?: string;
    parentContext?: string;
  }): AnalyzedEntity {
    const firstItem = entity.data[0];
    const fields = this.analyzeFieldsDeep(firstItem, entity.path);

    // Para arrays de primitivos, personalizar os nomes das colunas
    let columns: DataGridColumn[];
    if (entity.isPrimitiveArray && entity.originalFieldName) {
      // Criar colunas com nomes personalizados
      columns = [
        {
          field: 'index',
          header: '#',
          type: 'number',
          dataPath: 'index',
          width: '80px',
          sortable: true,
          filterable: false,
          editable: false
        },
        {
          field: 'value',
          header: this.formatLabel(entity.originalFieldName.replace('_list', '')), // Remove "_list" do nome
          type: typeof firstItem.value === 'number' ? 'number' : 'text',
          dataPath: 'value',
          width: '300px',
          sortable: true,
          filterable: true,
          editable: false
        }
      ];
    } else {
      columns = this.generateColumns(fields);
    }

    console.log(`  📝 Analisando entidade "${entity.name}":`, {
      isPrimitiveArray: entity.isPrimitiveArray || false,
      fields: fields.length,
      columns: columns.length
    });

    return {
      name: entity.name,
      dataPath: entity.path,
      fields: fields,
      sampleData: entity.data,
      suggestedColumns: columns,
      suggestedFormFields: this.generateFormFields(fields),
      level: entity.level,
      parentEntity: entity.parentEntity,
      childEntities: [], // Será populado depois
      // Adicionar propriedades para arrays de primitivos
      isPrimitiveArray: entity.isPrimitiveArray,
      originalFieldName: entity.originalFieldName,
      parentContext: entity.parentContext
    };
  }

  /**
   * ⭐ NOVO: Analisa campos de um objeto com suporte melhorado para arrays
   */
  private analyzeFieldsDeep(obj: any, parentPath: string = ''): AnalyzedField[] {
    const fields: AnalyzedField[] = [];

    for (const key in obj) {
      const value = obj[key];
      const dataPath = key; // IMPORTANTE: dataPath relativo ao item, não absoluto

      // Campo aninhado (objeto simples, não array)
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const nestedFields = this.analyzeFieldsDeep(value, dataPath);
        
        // Se objeto aninhado tem poucos campos (<=3), "achatar" como colunas
        if (nestedFields.length <= 3) {
          fields.push({
            name: key,
            type: 'text',
            dataPath: dataPath,
            label: this.formatLabel(key),
            isNested: true,
            nestedFields: nestedFields,
            sampleValue: value
          });
        } else {
          // Muitos campos: manter como objeto aninhado
          fields.push({
            name: key,
            type: 'text',
            dataPath: dataPath,
            label: this.formatLabel(key),
            isNested: true,
            nestedFields: nestedFields,
            sampleValue: value
          });
        }
      }
      // Array de objetos
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const firstItem = value[0];
        const arrayColumns = this.generateColumnsForArray(firstItem);
        
        console.log(`    📊 Array de objetos detectado: "${key}" com ${arrayColumns.length} colunas`);
        
        fields.push({
          name: key,
          type: 'select',
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: false,
          isArray: true,
          sampleValue: value,
          arrayColumns: arrayColumns,
          arrayData: value
        });
      }
      // Array de primitivos (strings, numbers)
      // MUDANÇA: Não adicionar como campo, pois será uma entidade separada (DataGrid próprio)
      else if (Array.isArray(value) && value.length > 0) {
        console.log(`    📝 Array de primitivos detectado: "${key}" - será DataGrid separado, não coluna`);
        // NÃO adicionar aos fields - será uma entidade/grid separado
        // Pulamos este campo para evitar criar coluna na grade principal
      }
      // Campo primitivo
      else {
        const type = this.inferFieldType(key, value);
        fields.push({
          name: key,
          type: type,
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: false,
          sampleValue: value
        });
      }
    }

    return fields;
  }

  /**
   * ⭐ NOVO: Gera colunas para um array de objetos
   */
  private generateColumnsForArray(firstItem: any): DataGridColumn[] {
    const columns: DataGridColumn[] = [];
    
    for (const key in firstItem) {
      const value = firstItem[key];
      
      // Array aninhado: mostrar como texto
      if (Array.isArray(value)) {
        columns.push({
          field: key,
          header: this.formatLabel(key),
          type: 'text',
          dataPath: key,
          width: '200px',
          sortable: false,
          filterable: false,
          editable: false
        });
      }
      // Objeto aninhado: achatar campos
      else if (typeof value === 'object' && value !== null) {
        for (const nestedKey in value) {
          columns.push({
            field: `${key}_${nestedKey}`,
            header: `${this.formatLabel(key)} - ${this.formatLabel(nestedKey)}`,
            type: this.mapToGridType(this.inferFieldType(nestedKey, value[nestedKey])),
            dataPath: `${key}.${nestedKey}`,
            width: this.suggestColumnWidth(this.inferFieldType(nestedKey, value[nestedKey])),
            sortable: true,
            filterable: true,
            editable: false
          });
        }
      }
      // Campo primitivo
      else {
        columns.push({
          field: key,
          header: this.formatLabel(key),
          type: this.mapToGridType(this.inferFieldType(key, value)),
          dataPath: key,
          width: this.suggestColumnWidth(this.inferFieldType(key, value)),
          sortable: true,
          filterable: true,
          editable: false
        });
      }
    }
    
    return columns;
  }

  /**
   * ⭐ NOVO: Sugere layout hierárquico baseado nas entidades encontradas
   * ATUALIZADO: Arrays de nível 2+ não criam tabs, vão para o panel do pai
   */
  private suggestLayoutDeep(entities: AnalyzedEntity[]): {
    tabs: { id: string; name: string; }[];
    panels: { id: string; name: string; tab: string; }[];
  } {
    const tabs: { id: string; name: string; }[] = [];
    const panels: { id: string; name: string; tab: string; }[] = [];

    // Estratégia:
    // - Nível 0 (raiz): Tab principal
    // - Nível 1 (filhos diretos): Tabs separadas
    // - Nível 2+ (netos, bisnetos): NÃO criam tabs, vão para DataGrids empilhados no panel do pai

    // 1. Encontrar entidade raiz (nível 0)
    const rootEntities = entities.filter(e => e.level === 0);
    
    if (rootEntities.length === 0) {
      console.warn('⚠️ Nenhuma entidade raiz encontrada!');
      return { tabs, panels };
    }

    const rootEntity = rootEntities[0];
    const rootTabId = `tab_${rootEntity.name.toLowerCase()}_main`;

    // Tab principal
    tabs.push({
      id: rootTabId,
      name: `${rootEntity.name}`
    });

    // Panel principal para o DataGrid raiz
    panels.push({
      id: `${rootTabId}_grid`,
      name: `Lista de ${rootEntity.name}`,
      tab: rootTabId
    });

    // 2. ⭐ Apenas arrays de NÍVEL 1 **de objetos** ganham tabs separadas
    // Arrays de primitivos em qualquer nível vão para o panel do pai
    const level1Entities = entities.filter(e => 
      e.level === 1 && !e.isPrimitiveArray // Excluir arrays de primitivos
    );
    
    level1Entities.forEach(childEntity => {
      const childTabId = `tab_${rootEntity.name.toLowerCase()}_${childEntity.name.toLowerCase()}`;
      
      tabs.push({
        id: childTabId,
        name: childEntity.name
      });

      panels.push({
        id: `${childTabId}_grid`,
        name: `Lista de ${childEntity.name}`,
        tab: childTabId
      });
    });

    // 3. ⭐ Arrays de NÍVEL 2+ E arrays de primitivos de qualquer nível NÃO criam tabs
    const embeddedEntities = entities.filter(e => 
      e.level >= 2 || e.isPrimitiveArray // Incluir todos arrays de primitivos
    );
    if (embeddedEntities.length > 0) {
      console.log(`  ℹ️ ${embeddedEntities.length} entidade(s) serão renderizadas como DataGrids empilhados (sem tabs próprias):`, 
        embeddedEntities.map(e => `${e.name}${e.isPrimitiveArray ? ' (array de primitivos)' : ''}`));
    }

    console.log(`📐 Layout sugerido: ${tabs.length} tabs, ${panels.length} panels`);

    return { tabs, panels };
  }

  /**
   * Analisa campos de um objeto
   */
  private analyzeFields(obj: any, parentPath: string = ''): AnalyzedField[] {
    const fields: AnalyzedField[] = [];

    for (const key in obj) {
      const value = obj[key];
      const dataPath = parentPath ? `${parentPath}.${key}` : key;

      // Campo aninhado (objeto)
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        fields.push({
          name: key,
          type: 'text',
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: true,
          nestedFields: this.analyzeFields(value, dataPath),
          sampleValue: value
        });
      }
      // Campo array (relacionamento)
      else if (Array.isArray(value)) {
        // NOVO: Detectar se é array de objetos e criar colunas para DataGrid
        let arrayColumns: DataGridColumn[] = [];
        
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          // Array de objetos - gerar colunas baseadas no primeiro item
          const firstItem = value[0];
          arrayColumns = Object.keys(firstItem).map(colKey => ({
            field: colKey,
            header: this.formatLabel(colKey),
            type: this.mapToGridType(this.inferFieldType(colKey, firstItem[colKey])),
            dataPath: colKey,
            width: this.suggestColumnWidth(this.inferFieldType(colKey, firstItem[colKey])),
            sortable: true,
            filterable: true,
            editable: false
          }));
          
          console.log(`📊 Array detectado: ${key} com ${value.length} itens e ${arrayColumns.length} colunas`);
        }
        
        fields.push({
          name: key,
          type: 'select',
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: false,
          isArray: true,
          sampleValue: value,
          // NOVO: Adicionar informações de colunas e dados
          arrayColumns: arrayColumns,
          arrayData: value
        });
      }
      // Campo primitivo
      else {
        const type = this.inferFieldType(key, value);
        fields.push({
          name: key,
          type: type,
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: false,
          sampleValue: value
        });
      }
    }

    return fields;
  }

  /**
   * Infere tipo de campo baseado em nome e valor
   */
  private inferFieldType(fieldName: string, value: any): 'text' | 'number' | 'date' | 'boolean' | 'textarea' {
    const name = fieldName.toLowerCase();

    // Boolean
    if (typeof value === 'boolean') return 'boolean';

    // Number
    if (typeof value === 'number') return 'number';

    // Date
    if (name.includes('date') || name.includes('time') || name.includes('createdat') || name.includes('updatedat')) {
      return 'date';
    }

    // Textarea (campos longos)
    if (name.includes('description') || name.includes('notes') || name.includes('comment') || name.includes('obs')) {
      return 'textarea';
    }

    // String longa = textarea
    if (typeof value === 'string' && value.length > 100) {
      return 'textarea';
    }

    // Default: text
    return 'text';
  }

  /**
   * Gera colunas para DataGrid
   */
  private generateColumns(fields: AnalyzedField[]): DataGridColumn[] {
    const columns: DataGridColumn[] = [];

    for (const field of fields) {
      // Não incluir arrays na grade
      if (field.isArray) continue;

      // Campos aninhados: criar coluna para cada sub-campo (até 1 nível)
      if (field.isNested && field.nestedFields) {
        for (const nestedField of field.nestedFields) {
          columns.push({
            field: `${field.name}_${nestedField.name}`,
            header: `${field.label} - ${nestedField.label}`,
            type: this.mapToGridType(nestedField.type),
            dataPath: nestedField.dataPath,
            width: this.suggestColumnWidth(nestedField.type),
            sortable: true,
            filterable: nestedField.type === 'text' || nestedField.type === 'select',
            editable: nestedField.type !== 'date'
          });
        }
      }
      // Campo simples
      else {
        columns.push({
          field: field.name,
          header: field.label,
          type: this.mapToGridType(field.type),
          dataPath: field.dataPath,
          width: this.suggestColumnWidth(field.type),
          sortable: true,
          filterable: field.type === 'text' || field.type === 'select',
          editable: field.type !== 'date'
        });
      }
    }

    return columns;
  }

  /**
   * Gera campos de formulário
   */
  private generateFormFields(fields: AnalyzedField[]): BuilderNode[] {
    const nodes: BuilderNode[] = [];
    let rowCounter = 0;

    for (const field of fields) {
      // Arrays: não incluir no formulário por enquanto
      if (field.isArray) continue;

      // Campos aninhados
      if (field.isNested && field.nestedFields) {
        // Adicionar cada campo aninhado
        for (const nestedField of field.nestedFields) {
          nodes.push(this.createFieldNode(nestedField, rowCounter++));
        }
      }
      // Campo simples
      else {
        nodes.push(this.createFieldNode(field, rowCounter++));
      }
    }

    return nodes;
  }

  /**
   * Cria um BuilderNode para um campo
   */
  private createFieldNode(field: AnalyzedField, row: number): BuilderNode {
    const nodeId = `field_${field.name}_${Date.now()}_${Math.random()}`;

    const baseProps: any = {
      label: field.label,
      placeholder: `Digite ${field.label.toLowerCase()}...`,
      dataPath: field.dataPath,
      required: field.name === 'id' ? false : true,
      colSpan: field.type === 'textarea' ? 24 : 12
    };

    // Props específicas por tipo
    let specificProps: any = {};

    switch (field.type) {
      case 'number':
        specificProps = { min: 0, step: 1 };
        break;
      case 'date':
        specificProps = { format: 'dd/MM/yyyy' };
        break;
      case 'boolean':
        specificProps = { checkedLabel: 'Sim', uncheckedLabel: 'Não' };
        break;
      case 'textarea':
        specificProps = { rows: 4 };
        break;
    }

    return {
      id: nodeId,
      type: field.type as BuilderComponentType,
      props: { ...baseProps, ...specificProps },
      parentId: null,
      tabId: null
    };
  }

  /**
   * Sugere layout de tabs e panels - MELHORADO para JSONs complexos
   */
  private suggestLayout(entities: AnalyzedEntity[]): {
    tabs: { id: string; name: string; }[];
    panels: { id: string; name: string; tab: string; }[];
  } {
    const tabs: { id: string; name: string; }[] = [];
    const panels: { id: string; name: string; tab: string; }[] = [];

    // Para cada entidade, criar tabs baseadas na estrutura de dados
    entities.forEach((entity, index) => {
      const entityName = entity.name.toLowerCase();
      
      // NOVO: Verificar se a entidade é um array (será renderizado como DataGrid)
      const isEntityAnArray = Array.isArray(entity.sampleData) && entity.sampleData.length > 0;
      
      // Separar campos em categorias
      const simpleFields = entity.fields.filter(f => !f.isNested && !f.isArray);
      const nestedObjects = entity.fields.filter(f => f.isNested && !f.isArray);
      const arrayFields = entity.fields.filter(f => f.isArray);

      // 1. Tab Principal: Informações Básicas
      const mainTabId = `tab_${entityName}_main`;
      tabs.push({
        id: mainTabId,
        name: `${entity.name} - Info`
      });

      panels.push({
        id: `${mainTabId}_basic`,
        name: `Informações Básicas`,
        tab: mainTabId
      });

      // 2. Para cada objeto aninhado, criar uma tab separada
      // CORREÇÃO: Não criar tabs para objetos aninhados se a entidade é um array
      // Quando é array, o DataGrid já terá colunas para os campos aninhados
      if (!isEntityAnArray) {
        nestedObjects.forEach((nested) => {
          const nestedTabId = `tab_${entityName}_${nested.name.toLowerCase()}`;
          tabs.push({
            id: nestedTabId,
            name: nested.label
          });

          panels.push({
            id: `${nestedTabId}_panel`,
            name: nested.label,
            tab: nestedTabId
          });
        });
      } else {
        console.log(`⏭️ Pulando criação de tabs para objetos aninhados de "${entity.name}" (é um array)`);
      }

      // 3. Para cada array (relacionamento), criar uma tab com DataGrid
      arrayFields.forEach((arrayField) => {
        const arrayTabId = `tab_${entityName}_${arrayField.name.toLowerCase()}`;
        tabs.push({
          id: arrayTabId,
          name: arrayField.label
        });

        panels.push({
          id: `${arrayTabId}_grid`,
          name: `Lista de ${arrayField.label}`,
          tab: arrayTabId
        });
      });
    });

    return { tabs, panels };
  }

  // ===== Utility Methods =====

  private formatEntityName(key: string): string {
    // "customers" -> "Customers"
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  private formatLabel(fieldName: string): string {
    // "firstName" -> "First Name"
    // "customer_name" -> "Customer Name"
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }

  private mapToGridType(fieldType: string): 'text' | 'number' | 'date' | 'boolean' {
    if (fieldType === 'textarea' || fieldType === 'select') return 'text';
    return fieldType as 'text' | 'number' | 'date' | 'boolean';
  }

  private suggestColumnWidth(type: string): string {
    switch (type) {
      case 'boolean': return '100px';
      case 'number': return '120px';
      case 'date': return '150px';
      case 'textarea': return '300px';
      default: return '200px';
    }
  }

  private findRootDataPath(json: any): string {
    // Assumir que a primeira chave é o root path
    const keys = Object.keys(json);
    return keys.length > 0 ? keys[0] : '';
  }
}

